import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import LoadingScreen from '../components/ui/LoadingScreen';
import PageHeader from '../components/ui/PageHeader';
import OAuthSetupGuide from '../components/OAuthSetupGuide';
import { getProductId } from '../utils/ids';

function OAuthConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(''); // Empty string means default
  const isInitialLoad = useRef(true);
  const [config, setConfig] = useState({
    productId: '',
    googleClientId: '',
    googleClientSecret: '',
    googleRedirectUrl: '',
    facebookClientId: '',
    facebookClientSecret: '',
    facebookRedirectUrl: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Load initial config when products are loaded
  useEffect(() => {
    if (!loading && products.length > 0 && isInitialLoad.current) {
      fetchOAuthConfig().finally(() => {
        isInitialLoad.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, products.length]);

  // Fetch config when product selection changes (user-initiated)
  useEffect(() => {
    if (!loading && !isInitialLoad.current) {
      fetchOAuthConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId]);

  const fetchProducts = async () => {
    try {
      const response = await api.post('/product/list', {});
      const productsList = response.data.data?.products || [];
      setProducts(productsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchOAuthConfig = async () => {
    try {
      const body = selectedProductId ? { productId: selectedProductId } : {};
      const response = await api.post('/admin/oauth-config/get', body);
      const existingConfig = response.data.data?.config;
      
      if (existingConfig) {
        const configProductId = existingConfig.productId || '';
        setConfig({
          productId: configProductId,
          googleClientId: existingConfig.googleClientId || '',
          googleClientSecret: '', // Don't populate secret for security
          googleRedirectUrl: existingConfig.googleRedirectUrl || '',
          facebookClientId: existingConfig.facebookClientId || '',
          facebookClientSecret: '', // Don't populate secret for security
          facebookRedirectUrl: existingConfig.facebookRedirectUrl || '',
        });
        
        if (isInitialLoad.current && selectedProductId === '' && configProductId !== '') {
          setSelectedProductId(configProductId);
        }
      } else {
        setConfig({
          productId: selectedProductId || '',
          googleClientId: '',
          googleClientSecret: '',
          googleRedirectUrl: '',
          facebookClientId: '',
          facebookClientSecret: '',
          facebookRedirectUrl: '',
        });
      }
    } catch (error) {
      console.error('Error fetching OAuth config:', error);
      setConfig({
        productId: selectedProductId || '',
        googleClientId: '',
        googleClientSecret: '',
        googleRedirectUrl: '',
        facebookClientId: '',
        facebookClientSecret: '',
        facebookRedirectUrl: '',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value,
    }));
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const configData = {
        googleClientId: config.googleClientId,
        googleClientSecret: config.googleClientSecret,
        googleRedirectUrl: config.googleRedirectUrl,
        facebookClientId: config.facebookClientId,
        facebookClientSecret: config.facebookClientSecret,
        facebookRedirectUrl: config.facebookRedirectUrl,
        productId: selectedProductId || null,
      };
      
      await api.post('/admin/oauth-config/update', configData);
      setMessage({ type: 'success', text: 'OAuth configuration saved successfully!' });
      await fetchOAuthConfig();
    } catch (error) {
      console.error('Error saving OAuth config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save OAuth configuration',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTesting(true);
    setTestingProvider(provider);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/admin/oauth-config/test', {
        productId: selectedProductId || null,
        provider: provider,
      });
      const isValid = response.data.data?.valid;
      
      if (isValid) {
        setMessage({ type: 'success', text: `${provider === 'google' ? 'Google' : 'Facebook'} OAuth configuration is valid!` });
      } else {
        setMessage({ type: 'error', text: `${provider === 'google' ? 'Google' : 'Facebook'} OAuth configuration is invalid or missing required fields.` });
      }
    } catch (error) {
      console.error('Error testing OAuth config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || `Failed to test ${provider === 'google' ? 'Google' : 'Facebook'} OAuth configuration`,
      });
    } finally {
      setTesting(false);
      setTestingProvider(null);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading OAuth configuration..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="OAuth Configuration"
        description="Configure Google and Facebook OAuth for user authentication."
      />

      {message.text && (
        <div className={`alert alert--${message.type === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      <OAuthSetupGuide />

      <div className="card card__body">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2 className="form-section__title">Product Selection</h2>

            <div className="form-group">
              <label htmlFor="productId" className="form-label">
                Product <span className="required">*</span>
              </label>
              <select
                id="productId"
                name="productId"
                className="form-select"
                value={selectedProductId}
                onChange={handleProductChange}
                required
              >
                <option value="">Select product — default applies to all products</option>
                {products.map((product) => {
                  const productId = getProductId(product);
                  return (
                    <option key={productId} value={productId}>
                    {product.name}
                  </option>
                  );
                })}
              </select>
              <span className="form-hint">Select a product for product-specific OAuth, or leave as Default for global settings.</span>
            </div>
          </div>

          <div className="form-section">
            <h2 className="form-section__title">Google OAuth Settings</h2>

            <div className="form-group">
              <label htmlFor="googleClientId" className="form-label">
                Google Client ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="googleClientId"
                name="googleClientId"
                className="form-input"
                value={config.googleClientId}
                onChange={handleChange}
                placeholder="Enter Google client ID"
              />
              <span className="form-hint">Google Cloud Console → APIs & Services → Credentials</span>
            </div>

            <div className="form-group">
              <label htmlFor="googleClientSecret" className="form-label">
                Google Client Secret <span className="required">*</span>
              </label>
              <input
                type="password"
                id="googleClientSecret"
                name="googleClientSecret"
                className="form-input"
                value={config.googleClientSecret}
                onChange={handleChange}
                placeholder="Enter Google client secret"
              />
              <span className="form-hint">Leave blank to keep existing secret unchanged</span>
            </div>

            <div className="form-group">
              <label htmlFor="googleRedirectUrl" className="form-label">
                Google Redirect URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="googleRedirectUrl"
                name="googleRedirectUrl"
                className="form-input"
                value={config.googleRedirectUrl}
                onChange={handleChange}
                placeholder="Enter Google redirect URL"
              />
              <span className="form-hint">Must match the redirect URI in Google Cloud Console</span>
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => handleTest('google')}
                className="btn btn--secondary"
                disabled={saving || testing}
              >
                {testing && testingProvider === 'google' ? 'Testing...' : 'Test Google Config'}
              </button>
            </div>
          </div>

          <div className="form-section">
            <h2 className="form-section__title">Facebook OAuth Settings</h2>

            <div className="form-group">
              <label htmlFor="facebookClientId" className="form-label">
                Facebook App ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="facebookClientId"
                name="facebookClientId"
                className="form-input"
                value={config.facebookClientId}
                onChange={handleChange}
                placeholder="Enter Facebook app ID"
              />
              <span className="form-hint">Facebook Developers Console → Settings → Basic</span>
            </div>

            <div className="form-group">
              <label htmlFor="facebookClientSecret" className="form-label">
                Facebook App Secret <span className="required">*</span>
              </label>
              <input
                type="password"
                id="facebookClientSecret"
                name="facebookClientSecret"
                className="form-input"
                value={config.facebookClientSecret}
                onChange={handleChange}
                placeholder="Enter Facebook app secret"
              />
              <span className="form-hint">Leave blank to keep existing secret unchanged</span>
            </div>

            <div className="form-group">
              <label htmlFor="facebookRedirectUrl" className="form-label">
                Facebook Redirect URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="facebookRedirectUrl"
                name="facebookRedirectUrl"
                className="form-input"
                value={config.facebookRedirectUrl}
                onChange={handleChange}
                placeholder="Enter Facebook redirect URL"
              />
              <span className="form-hint">Must match the redirect URI in Facebook App Settings</span>
            </div>

            <div className="form-group">
              <button
                type="button"
                onClick={() => handleTest('facebook')}
                className="btn btn--secondary"
                disabled={saving || testing}
              >
                {testing && testingProvider === 'facebook' ? 'Testing...' : 'Test Facebook Config'}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={saving || testing}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OAuthConfig;

